import React from "react";
import moment from "moment";
import i18next from "i18next";
import { PayoutStatusEnum } from "../../../../services/paymentModule";
import { formatPayModDate } from "../../../../services/planChange";
import { StatusBadge, BadgeTone } from "../../../atoms/StatusBadge";

const STATUS_TONE: Record<string, BadgeTone> = {
  PAID: "success",
  ACTIVE: "success",
  PENDING: "warning",
  RESTRICTED: "warning",
  PROCESSING: "info",
  FAILED: "failure",
  REJECTED: "failure",
  DISABLED: "failure",
  INACTIVE: "gray",
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Active",
  DISABLED: "Disabled",
  FAILED: "Failed",
  INACTIVE: "Inactive",
  PAID: "Paid",
  PENDING: "Pending",
  PROCESSING: "Processing",
  REJECTED: "Rejected",
  RESTRICTED: "Restricted",
};

const METHOD_LABELS: Record<string, string> = {
  BANK_TRANSFER: "Bank transfer",
  CRYPTO: "Crypto",
  STRIPE_CONNECT: "Stripe Connect",
};

export const humanizeEnum = (value?: string | null) => {
  if (!value) return "N/A";
  const words = value.replace(/_/g, " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const getStatusLabel = (status?: string | null): string => {
  if (!status) return "N/A";
  return STATUS_LABELS[status.toUpperCase()] ?? humanizeEnum(status);
};

export const getMethodLabel = (method?: string | null): string => {
  if (!method) return "N/A";
  return METHOD_LABELS[method.toUpperCase()] ?? humanizeEnum(method);
};

export const statusTone = (status?: string | null): BadgeTone =>
  STATUS_TONE[(status || "").toUpperCase()] || "gray";

export const getStatusBadge = (status: PayoutStatusEnum | string) => (
  <StatusBadge color={statusTone(status)}>{getStatusLabel(status)}</StatusBadge>
);

export const getMethodBadge = (method?: string | null) => (
  <StatusBadge color="gray">{getMethodLabel(method)}</StatusBadge>
);

export const getAccountStatusPill = (status?: string | null) => (
  <StatusBadge color={statusTone(status)}>{getStatusLabel(status)}</StatusBadge>
);

export const formatDateTime = (dateString: string | null) =>
  formatPayModDate(dateString, "L LT", "N/A");

export const formatDate = (dateString: string | null) =>
  dateString ? moment(dateString).format("L") : "N/A";

export const formatAmount = (amount: number, currency: string) => {
  const amountInUnits = amount / 100;
  return new Intl.NumberFormat(i18next.language || "en", {
    style: "currency",
    currency: currency || "EUR",
  }).format(amountInUnits);
};
